import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import FlightOptionCard from "@/components/cards/FlightOptionCard";
import { SourcePill, HeaderAccessory } from "@/components/cards/_pills";
import { resolveDisplay, formatPrice } from "@/lib/currency";
import { useGeo } from "@/context/GeoContext";
import { createWatch } from "@/lib/engagement";

const DEFAULT_VISIBLE = 4;

const optAmount = (o) => Number(o?.price?.amount ?? Infinity);
const optDuration = (o) => Number(o?.duration_minutes ?? Infinity);

export default function FlightGroupCard({ card, onPlacesToSee }) {
    const {
        title,
        group,
        options = [],
        live,
        source = "travelpayouts",
        source_label = "Travelpayouts",
        affiliate_network,
        alt_network_label,
        destination_weather,
        sandbox,
    } = card;
    const { t } = useTranslation();
    const { currencyPref } = useGeo();
    const [expanded, setExpanded] = useState(false);
    const [sortMode, setSortMode] = useState("best");
    const [nonStopOnly, setNonStopOnly] = useState(false);

    const hasNonStop = useMemo(
        () => options.some((o) => Number(o?.stops ?? 1) === 0),
        [options],
    );

    // Phase 15 — interactive sort/filter. "Best" preserves backend
    // ranking; "Cheapest"/"Fastest" re-sort client-side; "Non-stop"
    // filters. Mirrors mobile FlightGroupCard.
    const shaped = useMemo(() => {
        let list = [...options];
        if (nonStopOnly) list = list.filter((o) => Number(o?.stops ?? 1) === 0);
        if (sortMode === "price") list.sort((a, b) => optAmount(a) - optAmount(b));
        else if (sortMode === "duration") list.sort((a, b) => optDuration(a) - optDuration(b));
        return list;
    }, [options, sortMode, nonStopOnly]);

    // Smart insights strip — cheapest fare, fastest duration, savings.
    const insights = useMemo(() => {
        if (!options.length) return null;
        const cheapest = [...options].sort((a, b) => optAmount(a) - optAmount(b))[0];
        const fastest = [...options].sort((a, b) => optDuration(a) - optDuration(b))[0];
        if (!cheapest || optAmount(cheapest) === Infinity) return null;
        const cd = resolveDisplay(cheapest.price, cheapest.display_price, currencyPref);
        const parts = [`${t("cards.cheapest")} ${formatPrice(cd.display)}`];
        if (fastest && optDuration(fastest) !== Infinity) {
            const h = Math.floor(optDuration(fastest) / 60);
            const m = Math.round(optDuration(fastest) % 60);
            parts.push(`Fastest ${h ? `${h}h ` : ""}${m ? `${m}m` : ""}`.trim());
            const delta = optAmount(fastest) - optAmount(cheapest);
            if (delta > 0 && cheapest.id !== fastest.id) {
                const dd = resolveDisplay({ ...cheapest.price, amount: delta }, undefined, currencyPref);
                parts.push(`Save ${formatPrice(dd.display)} vs fastest`);
            }
        }
        const ns = options.filter((o) => Number(o?.stops ?? 1) === 0).length;
        if (ns > 0) parts.push(`${ns} non-stop`);
        return parts.join("  ·  ");
    }, [options, currencyPref, t]);

    const visible = expanded ? shaped : shaped.slice(0, DEFAULT_VISIBLE);
    const hiddenCount = Math.max(shaped.length - DEFAULT_VISIBLE, 0);

    // Phase 16 — price watch chip.
    const [watched, setWatched] = useState(false);
    const watchable = options[0]?.origin?.code && options[0]?.destination?.code;
    const handleWatch = async () => {
        if (watched || !watchable) return;
        try {
            const o = options[0];
            const cheapest = [...options].sort((a, b) => optAmount(a) - optAmount(b))[0];
            await createWatch({
                origin: o.origin.code,
                destination: o.destination.code,
                depart_date: o.origin?.date || null,
                currency: (cheapest?.price?.currency || "USD").toUpperCase(),
                label: `${o.origin.city || o.origin.code} → ${o.destination.city || o.destination.code}`,
                baseline_amount: cheapest?.price?.amount ?? null,
            });
            setWatched(true);
        } catch { /* best-effort */ }
    };

    const destCity = options[0]?.destination?.city;
    const originCity = options[0]?.origin?.city;
    const depDate = options[0]?.origin?.date;
    const returnDate = (() => {
        if (!depDate) return null;
        const d = new Date(depDate);
        if (Number.isNaN(d.getTime())) return null;
        d.setDate(d.getDate() + 3);
        return d.toISOString().slice(0, 10);
    })();

    return (
        <section className="space-y-3" data-testid={`flight-group-${group}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2">
                    <h2 className="text-[18px] font-semibold tracking-tight text-[#0A0A0A]">
                        {title}
                    </h2>
                    {live && (
                        <SourcePill
                            source={source}
                            source_label={source_label}
                            affiliate_network={affiliate_network}
                            alt_network_label={alt_network_label}
                        />
                    )}
                </div>
                <HeaderAccessory
                    destinationWeather={destination_weather}
                    dotColor="#2563EB"
                />
            </div>

            {insights && (
                <p
                    className="mx-1 rounded-lg bg-[#F5F5F5] px-3 py-1.5 text-[12px] font-medium text-[#525252]"
                    data-testid={`flight-insights-${group}`}
                >
                    {insights}
                </p>
            )}

            {(options.length > 1 || watchable) && (
                <div className="flex flex-wrap items-center gap-1.5 px-1" data-testid={`flight-sort-row-${group}`}>
                    {watchable && (
                        <button
                            type="button"
                            onClick={handleWatch}
                            data-testid={`flight-watch-${group}`}
                            className={`rounded-full border px-3 py-1 text-[12px] font-medium transition ${
                                watched
                                    ? "border-[#BBF7D0] bg-[#F0FDF4] text-[#16A34A]"
                                    : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"
                            }`}
                        >
                            {watched ? "✓ Watching price" : "🔔 Watch price"}
                        </button>
                    )}
                    {options.length > 1 && [["best", "Best"], ["price", "Cheapest"], ["duration", "Fastest"]].map(([mode, label]) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => setSortMode(mode)}
                            data-testid={`flight-sort-${mode}-${group}`}
                            className={`rounded-full border px-3 py-1 text-[12px] font-medium transition ${
                                sortMode === mode
                                    ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                                    : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                    {hasNonStop && (
                        <button
                            type="button"
                            onClick={() => setNonStopOnly((v) => !v)}
                            data-testid={`flight-filter-nonstop-${group}`}
                            className={`rounded-full border px-3 py-1 text-[12px] font-medium transition ${
                                nonStopOnly
                                    ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                                    : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"
                            }`}
                        >
                            Non-stop
                        </button>
                    )}
                </div>
            )}

            <div className="space-y-3">
                {visible.map((opt, idx) => (
                    <FlightOptionCard
                        key={opt.id}
                        option={opt}
                        showCheapestBadge={idx === 0 && opt.is_cheapest}
                    />
                ))}
                {visible.length === 0 && (
                    <p className="px-1 py-2 text-center text-[12px] text-[#737373]" data-testid={`flight-filter-empty-${group}`}>
                        No non-stop flights on this route — clear the filter to see all options.
                    </p>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2 px-1">
                {hiddenCount > 0 && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`flight-toggle-${group}`}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                    >
                        {expanded ? (
                            <>
                                <ChevronUp size={14} strokeWidth={2.25} /> Hide extras
                            </>
                        ) : (
                            <>
                                <ChevronDown size={14} strokeWidth={2.25} /> More options (+{hiddenCount})
                            </>
                        )}
                    </button>
                )}
                {destCity && onPlacesToSee && [
                    ["plan", `Plan itinerary in ${destCity}`,
                     `Plan a 3-day itinerary in ${destCity}`],
                    ["return", `Return ${destCity} → ${originCity || "back"}`,
                     `Flights from ${destCity} to ${originCity || "home"}${returnDate ? " on " + returnDate : ""}`],
                    ["hotels", `Hotels in ${destCity}`,
                     `Hotels in ${destCity}${depDate ? " for " + depDate + " 2 nights" : " for 2 nights"}`],
                    ["things", `Things to do in ${destCity}`,
                     `Things to do in ${destCity}`],
                ].map(([key, label, query]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onPlacesToSee(query)}
                        data-testid={`flight-cta-${key}-${group}`}
                        className="inline-flex items-center rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[12px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                    >
                        {label}
                    </button>
                ))}
            </div>

            {sandbox && (
                <p
                    className="px-1 text-[11px] tracking-wide text-[#A3A3A3]"
                    data-testid={`flight-sandbox-warning-${group}`}
                >
                    {t("cards.sandbox_warning")}
                </p>
            )}
        </section>
    );
}
